import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, firstValueFrom, take } from 'rxjs';
import { MapHelperService } from 'src/services/map-helper.service';
import { environment } from 'src/environments/environment';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { LayerAPIService } from 'src/app/features/shared/services/API/LayersAPI.service';
import { ProspectDetailsPopupComponent } from '../../locations-section/components/3-panel-detail-locations/organisms/location-popup-details/prospect-details-popup/prospect-details-popup.component';
import { HierarchyLevel } from '../../../ui/organisms/polygon-selector-with-levels/models/polygon-selector-with-levels.models';
import { EnumPolygonFillType } from '../../../ui/organisms/polygon-selector/helpers/enums/EnumPolygonFillType.enum';
import { LocationCategoryDTO } from '../../locations-section/models/LocationCategoryDTO';

@Injectable({
  providedIn: 'root'
})
export class ProspectsLayerControlService {
  hierarchyLevels: HierarchyLevel[] = [];

  private sourcePrefix = 'polygon-selector-source-prospects-';
  private layerPrefix = 'layer-polygon-selector-prospects-';
  private layerLinePrefix = 'layer-line-polygon-selector-prospects-';
  private layerTextPrefix = 'layer-polygon-selector-text-prospects-';

  private locationSufix = 'prospects-zones';

  private readonly generalMapIconSize = 0.1011;

  private readonly locationIdProperty = '_Id';
  private locationTextProperty = 'Description';
  private locationIcon = 'bussineLocation.png';
  private locationActiveIcon = 'bussineLocationA.png';

  private levelLineColors: string[] = ['rgb(140, 0, 255)', 'rgb(34, 59, 83)', '#e55e5e', '#1329f0', '#51bbd6',];
  private levelFillColors: string[] = ['rgb(160, 200, 240)', 'rgb(144, 224, 240)', '#f9f4a5', '#627e90', '#f28b82'];

  private isInitialized: boolean = false;

  popUp: MatDialogRef<ProspectDetailsPopupComponent> = null;
  hoveredMarker: any = null;
  hoveredMarkerFeature: any = null;

  constructor(
    private mapHelperService: MapHelperService,
    private http: HttpClient,
    private layerAPIService: LayerAPIService,
    private dialog: MatDialog,
    private toastrService: ToastrService
  ) {}

  async loadLayersFromAPI(idMap: number): Promise<HierarchyLevel[]> {
    const resp = await firstValueFrom( this.layerAPIService.GetLayersByIdMap(idMap.toString()).pipe(take(1)) );

    if (!resp.isSuccess) {
      console.error("selector, error cargando capas desde API", resp);
      return [];
    }

    const layers = resp.result as any[];
    this.hierarchyLevels = [];

    const sortedLayers = layers.sort((a, b) => (a.idOrder || 0) - (b.idOrder || 0));

    sortedLayers.forEach((layer, index) => {
      const hierarchyLevel: HierarchyLevel = {
        key: layer.name || layer.id.toString(),
        path: layer.path || '',
        parentIdProperty: null,
        idProperty: layer.layerFilterControl_PropertyFilter || 'Id',
        sourceName: this.getSourceName(index),
        layerName: this.getLayerName(index),
        layerLineName: this.getLayerLineName(index),
        layerTextName: this.getLayerTextName(index)
      };
      this.hierarchyLevels.push(hierarchyLevel);
    });

    return this.hierarchyLevels;
  }

  getHierarchyLevels(): HierarchyLevel[] {
    return this.hierarchyLevels;
  }

  async createAllSourcesAndLayers(agebCloropetConfig: any = null, categoryDTO: LocationCategoryDTO = null, simpleSelectorPrefixAdd: string = ''): Promise<void> {
    if (!this.mapHelperService._map || !this.mapHelperService.isMainSourceReady || !this.mapHelperService.isStyleReady) {
        await firstValueFrom(
          this.mapHelperService.mainSource$.pipe(
            filter(state => state != null && state.isMainSourceReady && state.isStyleReady),
            take(1)
          )
        );
      }

    if (this.isInitialized) {
      return;
    }

    // crea sources y layers para los niveles iniciales
    this.hierarchyLevels.forEach((level, index) => {
      if (index >= this.hierarchyLevels.length -1)
        return;
      this.createSourceAndLayersForLevel(index);
    });

    this.createSimpleSelectorSourceAndLayers(simpleSelectorPrefixAdd, EnumPolygonFillType.Cloropet, '#627e90', agebCloropetConfig);
    this.createLocationsSourceAndLayers(this.locationSufix, categoryDTO);
    this.isInitialized = true;
  }

  private createSourceAndLayersForLevel(levelIndex: number): void {
    var sourceDisplayName = "";
    var sourceIcon = "";
    switch (levelIndex) {
      case 0:
        sourceDisplayName = "Region seleccionada";
        sourceIcon = 'annotation-potential-zones-region.png';
        break;
      case 1:
        sourceDisplayName = "Zona seleccionada";
        sourceIcon = 'annotation-potential-zones-zone.png';
        break;
      default:
        sourceDisplayName = "Prospectos nivel " + levelIndex;
        sourceIcon = 'annotation-polygon-selector.png';
        break;
    }
    const sourceName = this.getSourceName(levelIndex);
    const layerName = this.getLayerName(levelIndex);
    const layerLineName = this.getLayerLineName(levelIndex);
    const layerTextName = this.getLayerTextName(levelIndex);

    const existingSource = this.mapHelperService.GetSourceByName(sourceName);

    if (!existingSource) {
      const color = this.getColorForLevel(levelIndex, this.levelFillColors);
      const lineColor = this.getColorForLevel(levelIndex, this.levelLineColors);

      this.mapHelperService.AddSourceEmptyGeoJson(sourceName, sourceDisplayName, sourceIcon);
      this.mapHelperService.AddLayerPolygonsWithFeatureStatus(layerName, sourceName, "Polígonos de prospectos, nivel " + levelIndex, color);
      this.mapHelperService.AddLayerLine(layerLineName, sourceName, "Líneas de prospectos, nivel " + levelIndex, lineColor, 3);
      this.mapHelperService.AddLayerText(layerTextName, sourceName, 'Description', "Etiqueta de prospectos, nivel " + levelIndex, -1);

    } else {
      console.log("ya existe el source para nivel " + levelIndex);
    }
  }

  createSimpleSelectorSourceAndLayers(sourceNameAdd: string, fillType: string = 'solid', fillColor: string = '#627e90', cloropetConfig: any = null): void {
    const sourceName = 'polygon-selector-source-' + sourceNameAdd;
    const layerName = 'layer-polygon-selector-' + sourceNameAdd;
    const layerLineName = 'layer-line-polygon-selector-' + sourceNameAdd;
    const layerTextName = 'layer-polygon-selector-text-' + sourceNameAdd;
    const lineColor = '#352F84';

    const existingSource = this.mapHelperService.GetSourceByName(sourceName);

    if (!existingSource) {
      this.mapHelperService.AddSourceEmptyGeoJson(sourceName, "Agebs", 'annotation-potential-zones-agebs.png');

      if (fillType == EnumPolygonFillType.Cloropet && cloropetConfig) {
        this.mapHelperService.AddLayerPolygonsWithFeatureStatus(layerName, sourceName, "Poligonos de selección de agebs", fillColor, cloropetConfig);
      } else {
        this.mapHelperService.AddLayerPolygonsWithFeatureStatus(layerName, sourceName, "Poligonos de selección de agebs", fillColor);
        this.mapHelperService.AddLayerLine(layerLineName, sourceName, "Líneas de selección de agebs", lineColor, 3);
      }

      this.mapHelperService.AddLayerText(layerTextName, sourceName,  'Description', "Etiqueta de selección de agebs", -1);

    } else {
      console.log("ya existe el source simple");
    }
  }

  createLocationsSourceAndLayers(sourceNameAdd: string, categoryDTO: LocationCategoryDTO = null): void {
    const sourceName = 'locations-source-' + sourceNameAdd;
    const layerName = 'layer-locations-' + sourceNameAdd;
    const layerTextName = 'layer-locations-text-' + sourceNameAdd;

    const existingSource = this.mapHelperService.GetSourceByName(sourceName);

    if (!existingSource) {
      this.locationIcon = categoryDTO?.icon || this.locationIcon;
      this.locationActiveIcon = categoryDTO?.activeIcon || this.locationActiveIcon;

      this.mapHelperService.AddSourceClusteredEmptyGeoJson(sourceName, "Prospectos", this.locationIcon);
      this.mapHelperService.AddLayerCluster(layerName + "-layerCluster", sourceName, "Clusters de Prospectos");
      this.mapHelperService.AddLayerClusterCount( layerName + "-layerClusterCount", sourceName, "Conteo de Clusters de Prospectos");
      this.mapHelperService.AddLayerUnclusteredPointsV2( layerName, sourceName , this.locationIcon, "Puntos de Prospectos", this.generalMapIconSize );
      this.mapHelperService.AddLayerText(layerTextName, sourceName, this.locationTextProperty, "Etiqueta de Prospectos", -2);

      this.addEvent();
    } else {
      console.log("ya existe el source simple");
    }
  }

  addEvent() {
    const sourceName = 'locations-source-' + this.locationSufix;
    const layerName = 'layer-locations-' + this.locationSufix;
    const clusterLayerName = layerName + '-layerCluster';
    const layerTextName = 'layer-locations-text-' + this.locationSufix;
    const idProperty = this.locationIdProperty;

    this.mapHelperService.AddClusterZoomEvent(clusterLayerName, sourceName);

    const mouseClick = (e) => {
      if (e.features.length > 0) {
        const feature = e.features[0];
        const identifier = feature.properties ? feature.properties[idProperty] : '';
        const coords = feature.geometry.coordinates;

        const popupData = {
          identifier: identifier,
          parentCoordX: coords[0],
          parentCoordY: coords[1],
          UpdateActiveLayer: true,
        };
        this.openDetailsPopup(popupData);
      }

    };

    const layerMouseOver = (e) => {

      const marker = e.features[0].properties;

      this.hoveredMarker = marker;
      this.hoveredMarkerFeature = e.features[0];

      this.mapHelperService._map.setLayoutProperty(
        layerName,
        "icon-image",
        [
          "case",
          ["==", ["get", idProperty], marker[idProperty]],
          ['coalesce', ['get', 'LocationCategoryActiveIcon'], this.locationActiveIcon],
          ['coalesce', ['get', 'LocationCategoryIcon'], this.locationIcon],
        ]
      );

      // cambia el color del nombre
      this.mapHelperService._map.setPaintProperty(layerTextName, "text-color", [
        "case",
        ["==", ["get", idProperty], marker[idProperty]],
        "#0F010F",
        "#530aa8",
      ]);
    };

    const layerMouseLeave = () => {
      if (this.hoveredMarker) {
        if (!environment.production) console.log("hoveredMarkerId", this.hoveredMarker);

        this.mapHelperService._map.setLayoutProperty(
          layerName,
          "icon-image",
          ['coalesce', ['get', 'LocationCategoryIcon'], this.locationIcon]
        );
        this.mapHelperService._map.setPaintProperty(
          layerTextName,
          "text-color",
          "#530aa8"
        );

        this.hoveredMarker = null;
      }
    };

    const rightClick = (e) => {
      e.preventDefault();
      if (e.features.length > 0) {
        const feature = e.features[0];
        const lat = feature.geometry ? feature.geometry.coordinates[1] : '';
        const lng = feature.geometry ? feature.geometry.coordinates[0] : '';
        const coords = lat + ", " + lng;

        if (coords) {
          navigator.clipboard.writeText(coords).then(() => {
            this.toastrService.info('Coordenadas copiadas al portapapeles');
          }).catch(err => {
            console.error('Error al copiar al portapapeles:', err);
          });
        }
      }
    };

    this.mapHelperService._map.on('click', layerName, mouseClick);
    this.mapHelperService._map.on('contextmenu', layerName, rightClick);
    this.mapHelperService._map.on('mouseenter', layerName, layerMouseOver);
    this.mapHelperService._map.on('mouseleave', layerName, layerMouseLeave);
  }

  openDetailsPopup(popupData: any): MatDialogRef<ProspectDetailsPopupComponent> {
    this.closeDetailsPopup();

    const dialogRef = this.dialog.open(ProspectDetailsPopupComponent,
      {
        maxWidth: "98vw",
        maxHeight: "92vh",
        autoFocus: false,
        restoreFocus: false,
        hasBackdrop: false,
        disableClose: true,
        data: popupData,
      }
    );

    this.popUp = dialogRef;
    dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      if (this.popUp === dialogRef) {
        this.popUp = null;
      }
    });

    return dialogRef;
  }

  closeDetailsPopup(): void {
    if (this.popUp) {
      this.popUp.close();
      this.popUp = null;
    }
  }

  loadMoreLocationGeoJson(geoJson: any): void {
    const sourceName = 'locations-source-' + this.locationSufix;

    this.mapHelperService.UpdateLayerGeoJson(sourceName, geoJson);
  }

  updateCategoryVisuals(categoryDTO: LocationCategoryDTO): void {
    if (!categoryDTO || !this.mapHelperService._map) {
      return;
    }
    const layerName = 'layer-locations-' + this.locationSufix;
    const layerTextName = 'layer-locations-text-' + this.locationSufix;

    this.locationIcon = categoryDTO.icon || this.locationIcon;
    this.locationActiveIcon = categoryDTO.activeIcon || this.locationActiveIcon;

    try {
      this.mapHelperService._map.setLayoutProperty(layerName, 'icon-image', [ 'coalesce', ['get', 'LocationCategoryIcon'], this.locationIcon]);
      this.mapHelperService.UpdateLayerText(layerTextName, ["case",
        ["has", this.locationTextProperty],
        ["get", this.locationTextProperty],
        ["get", this.locationIdProperty]]);
    } catch (e) {
      console.log("error actualizando visuals de categoria de prospectos", e);
    }
  }

  sendLocationsToTop(): void {
    const layerName = 'layer-locations-' + this.locationSufix;
    const layerTextName = 'layer-locations-text-' + this.locationSufix;

    this.mapHelperService.MoveLayerToTop(layerName + '-layerCluster');
    this.mapHelperService.MoveLayerToTop(layerName + '-layerClusterCount');
    this.mapHelperService.MoveLayerToTop(layerName);
    this.mapHelperService.MoveLayerToTop(layerTextName);
  }

  clearLocations(): void {
    const sourceName = 'locations-source-' + this.locationSufix;
    this.mapHelperService.UpdateLayerGeoJson(sourceName, this.mapHelperService.Empty_GeoJson);

    this.closeDetailsPopup();
  }


  // métodos para obtener nombres
  getSourceName(levelIndex: number): string {
    return this.sourcePrefix + levelIndex;
  }

  getLayerName(levelIndex: number): string {
    return this.layerPrefix + levelIndex;
  }

  getLayerLineName(levelIndex: number): string {
    return this.layerLinePrefix + levelIndex;
  }

  getLayerTextName(levelIndex: number): string {
    return this.layerTextPrefix + levelIndex;
  }

  private getColorForLevel(levelIndex: number, colors: string[]): string {
    return colors[levelIndex % colors.length];
  }

  destroy(): void {
    this.isInitialized = false;
    this.clearLocations();
  }
}
